/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateNavigation, PayInfoModal } from 'components';
import { Input } from 'components/Input';
import { useCoupon, useCouponGroup, useCouponDetail, useMeInfo, useExcelDownload, useCancelCoupon } from 'hooks';
import { modalMethod } from 'components/Modal';
import moment from 'moment';
import { numberWithCommas } from 'utils/stringUtils';

import RealtimeTable from './RealtimeTable';

const Realtime = () => {
  const testId = 'hvendys';
  const { changeExcelParams } = useExcelDownload();
  const [date, setDate] = useState(new Date());
  const [paging, setPaging] = useState({ page: 1, pageRow: 15 });
  const [isOpen, setOpen] = useState<boolean>(false);
  const [payRoomIdxInput, setPayRoomIdxInput] = useState<string>('');
  const [appliedPayRoomIdx, setAppliedPayRoomIdx] = useState<string | undefined>(undefined);
  const shouldNotifyEmptyRef = useRef<boolean>(false);
  const skipEmptyNotifyRef = useRef<boolean>(false);
  const [couponId, setCouponId] = useState<string | undefined>();
  const [contents, setContents] = useState<Record<string, unknown>[]>([]);
  const { selectedStore, meInfo } = useMeInfo();
  const { data, refetch: refetchCoupon } = useCoupon(selectedStore?.sid, moment(date).format('YYYY-MM-DD'));
  const {
    data: realtimeSales,
    refetch: refetchCouponGroup,
    isFetching: isCouponGroupFetching,
    isFetched: isCouponGroupFetched
  } = useCouponGroup(paging.page, paging.pageRow, selectedStore?.sid, moment(date).format('YYYY-MM-DD'), appliedPayRoomIdx);
  const { data: detail, isLoading } = useCouponDetail(couponId, { sid: selectedStore?.sid });

  useEffect(() => {
    changeExcelParams({ date1: date, date2: date });
  }, [date, changeExcelParams]);

  // realtimeSales 응답에 따라 목록 갱신 (1페이지는 교체, 이후는 누적)
  useEffect(() => {
    const nextSales = (realtimeSales as any)?.sales ?? [];
    if (paging.page === 1) {
      setContents(nextSales);
    } else {
      setContents((prev) => [...prev, ...nextSales]);
    }
  }, [realtimeSales, paging.page]);

  useEffect(() => {
    if (paging.page !== 1 || !shouldNotifyEmptyRef.current) {
      return;
    }
    if (!isCouponGroupFetched || isCouponGroupFetching || !realtimeSales) {
      return;
    }

    const nextSales = (realtimeSales as any)?.sales ?? [];
    const totalCount = (realtimeSales as any)?.totalCount ?? 0;
    const hasSales = nextSales.length > 0;

    if (totalCount === 0 && !hasSales && !skipEmptyNotifyRef.current) {
      modalMethod.info(
        <>
          <div>매출 내역이 없습니다.</div>
          <div>결제번호, 결제일자를 다시한번 확인해주세요.</div>
        </>
      );
    }

    shouldNotifyEmptyRef.current = false;
    skipEmptyNotifyRef.current = false;
  }, [realtimeSales, paging.page, isCouponGroupFetched, isCouponGroupFetching]);
  // 결제취소 성공 후 데이터 새로고침을 위한 콜백 함수
  const handleRefreshData = useCallback(async () => {
    try {
      await Promise.all([refetchCoupon(), refetchCouponGroup()]);
      console.log('데이터 새로고침 완료');
    } catch (error) {
      console.warn('데이터 새로고침 중 에러 발생:', error);
    }
  }, [refetchCoupon, refetchCouponGroup]);
  const cancelCouponMutation = useCancelCoupon(handleRefreshData);

  const handleDateChange = (v: Date) => {
    // 날짜 변경 시 결제번호 검색 입력을 먼저 초기화하고, 적용된 검색 파라미터도 제거
    setPayRoomIdxInput('');
    setAppliedPayRoomIdx(undefined);
    shouldNotifyEmptyRef.current = false;
    skipEmptyNotifyRef.current = false;
    setPaging({ ...paging, page: 1 });
    setContents([]);
    setDate(v);
  };

  const handleSearch = (value?: string) => {
    const next = (value ?? payRoomIdxInput).trim();
    const willApply = next.length > 0 ? next : undefined;
    const pageChanged = paging.page !== 1;
    const keyChanged = appliedPayRoomIdx !== willApply;

    if (keyChanged || pageChanged) {
      shouldNotifyEmptyRef.current = true;
      setContents([]);
      setPaging({ ...paging, page: 1 });
      setAppliedPayRoomIdx(willApply);
      return;
    }

    // 쿼리키가 동일할 때는 목록을 비우지 않고 강제 재조회
    shouldNotifyEmptyRef.current = true;
    refetchCouponGroup();
  };

  const getMoreList = () => {
    setPaging({ ...paging, page: paging.page + 1 });
  };

  const openDetail = (selectedCouponId: string) => {
    setCouponId(selectedCouponId);
    setOpen(true);
  };

  const closeDetail = () => {
    setOpen(false);
  };

  const handleCancelPayment = async (selectedCouponId: string, cancelMessage: string) => {
    console.log('결제취소 요청:', selectedCouponId, '취소사유:', cancelMessage);

    // 결제취소 API 호출 (에러는 useCancelCoupon의 onError에서 처리)
    cancelCouponMutation.mutate(
      { couponId: selectedCouponId, cancelMessage },
      {
        onSuccess: () => {
          // 성공 시 알림 및 모달 닫기
          modalMethod.info('결제 및 주문취소가 완료 되었습니다.');
          closeDetail();
        }
        // onError는 훅에서 errorHandler로 처리됨
      }
    );
  };

  return (
    <div className="realtime-content" id="realtime">
      <DateNavigation date={date} onChange={handleDateChange} />
      <div className="priceInfo">
        <div className="salereport">
          <div className="left">총액</div>
          <div className="right">
            {numberWithCommas(data?.totalPrice || 0)} <span className="won">원</span>
          </div>
        </div>
      </div>
      {/* 검색 입력: Figma 선택 요소의 구조를 반영하여 큰 사이즈 입력 + 조회 버튼 UI */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
        <Input.Search
          placeholder="결제번호로 조회"
          allowClear
          size="large"
          value={payRoomIdxInput}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '' && payRoomIdxInput !== '') {
              // X 버튼 또는 전체 삭제로 빈 값이 된 경우, 다음 한 번은 빈 결과 팝업을 스킵
              skipEmptyNotifyRef.current = true;
            }
            setPayRoomIdxInput(v);
          }}
          onSearch={(v) => handleSearch(v)}
          enterButton="조회"
        />
      </div>
      <RealtimeTable
        isTestId={meInfo?.admin.id === testId}
        realtimeSales={contents}
        isMore={paging.page * paging.pageRow < (realtimeSales?.totalCount || 0)}
        getMore={getMoreList}
        onClick={openDetail}
      />
      <PayInfoModal
        isLoading={isLoading}
        open={isOpen}
        close={closeDetail}
        data={detail?.coupon}
        menus={detail?.menus}
        shippingFeeInfos={detail?.shippingFeeInfo ? [detail?.shippingFeeInfo] : []}
        supplyType={selectedStore?.supplyType}
        onCancelPayment={(selectedCouponId: string, cancelMessage: string) =>
          handleCancelPayment(selectedCouponId, cancelMessage)
        }
        isCancelable={detail?.isCancelable}
        hasNonRefundableReasons={detail?.hasNonRefundableReasons}
        nonRefundables={detail?.nonRefundables}
      />
    </div>
  );
};

export default Realtime;
